/**
 * m3 echo Frontend - Combined Timeline Chart Component (V3.0)
 *
 * Displays a dual Y-axis chart showing correlation between:
 * - LLM Test scores (average mention rate)
 * - Benchmark Test scores (overall score)
 *
 * Uses a date-based timeline where each test is its own data point.
 */

import React from 'react';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  Alert,
  Chip,
  AlertTitle,
  Tooltip as MuiTooltip,
} from '@mui/material';
import { CombinedTimeline } from '../types/api';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface CombinedTimelineChartProps {
  data: CombinedTimeline;
}

const CombinedTimelineChart: React.FC<CombinedTimelineChartProps> = ({ data }) => {
  const theme = useTheme();
  const reduceMotion = useReducedMotion();

  /**
   * Prepares data for the dual-axis chart.
   * Date-based timeline: each test (LLM or optimization) is its own data point.
   * Lines use connectNulls to bridge gaps.
   */
  const prepareChartData = () => {
    // Collect all items sorted chronologically
    const allItems = [...data.items]
      .filter(item => item.score !== null)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Normalize timestamps: append Z for naive UTC strings (consistent with rest of app)
    const parseDateUTC = (d: string) => {
      const s = d.endsWith('Z') ? d : d + 'Z';
      return new Date(s);
    };

    // Detect duplicate dates to add time when needed
    const dateCounts = new Map<string, number>();
    for (const item of allItems) {
      const d = parseDateUTC(item.date);
      const dateOnly = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
      dateCounts.set(dateOnly, (dateCounts.get(dateOnly) || 0) + 1);
    }

    return allItems.map((item) => {
      const date = parseDateUTC(item.date);
      const dateOnly = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });

      // Add time component when multiple tests share the same date
      const formattedDate = (dateCounts.get(dateOnly) || 0) > 1
        ? dateOnly + ' ' + date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
        : dateOnly;

      return {
        date: formattedDate,
        fullDate: date.toISOString(),
        type: item.type,
        llmScore: item.type === 'llm_test' ? item.score : null,
        optScore: item.type === 'optimization_test' ? item.score : null,
      };
    });
  };

  const chartData = prepareChartData();

  /**
   * Custom tooltip
   */
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      const testTypeText = data.type === 'llm_test' ? 'LLM-Test' : 'Optimierungs-Test';

      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            {testTypeText}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary" gutterBottom>
            {data.date}
          </Typography>
          {payload.map((entry: any, index: number) => {
            if (entry.value !== null) {
              return (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{ color: entry.color }}
                >
                  {entry.name}: {entry.value.toFixed(1)}
                </Typography>
              );
            }
            return null;
          })}
        </Box>
      );
    }
    return null;
  };

  const correlation = data.correlation ?? null;

  // Detect mixed V1/V2 scoring in optimization tests
  const optItems = data.items.filter(i => i.type === 'optimization_test');
  const hasV1 = optItems.some(i => !i.scoring_version || i.scoring_version < 2);
  const hasV2 = optItems.some(i => i.scoring_version && i.scoring_version >= 2);
  const hasMixedScoring = hasV1 && hasV2;

  /**
   * Get correlation description
   */
  const getCorrelationText = (r: number | null): { text: string; color: string } => {
    if (r === null) {
      return { text: 'Nicht genug Daten', color: 'grey' };
    }

    const absR = Math.abs(r);
    if (absR >= 0.7) {
      return {
        text: `Starke ${r > 0 ? 'positive' : 'negative'} Korrelation`,
        color: r > 0 ? 'success' : 'error',
      };
    } else if (absR >= 0.4) {
      return {
        text: `Mittlere ${r > 0 ? 'positive' : 'negative'} Korrelation`,
        color: r > 0 ? 'info' : 'warning',
      };
    } else {
      return { text: 'Schwache Korrelation', color: 'default' };
    }
  };

  const correlationInfo = getCorrelationText(correlation);

  if (chartData.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Kombinierte Analyse
          </Typography>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={300}
          >
            <Typography color="text.secondary">
              Noch keine Daten für kombinierte Analyse verfügbar
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MuiTooltip title="Zeigt den Zusammenhang zwischen durchgeführten Optimierungsmaßnahmen und der Entwicklung des LLM-Scores. Die Korrelation (Pearson's r) misst, ob Optimierungen zu besseren Rankings führen." arrow>
            <Typography variant="h6" sx={{ cursor: 'help' }}>
              Kombinierte Analyse: LLM-Tests & Optimierung
            </Typography>
          </MuiTooltip>
          {correlation !== null && (
            <Chip
              label={`r = ${correlation.toFixed(3)} - ${correlationInfo.text}`}
              color={correlationInfo.color as any}
              size="small"
            />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Korrelation zwischen Optimierungs-Maßnahmen und LLM-Ranking-Verbesserungen
        </Typography>

        {correlation !== null && Math.abs(correlation) >= 0.4 && (
          <Alert severity={correlation > 0 ? 'success' : 'warning'} sx={{ mt: 2, mb: 2 }}>
            {correlation > 0
              ? 'Positive Korrelation: Optimierungs-Verbesserungen korrelieren mit höheren LLM-Rankings!'
              : 'Negative Korrelation: Weitere Analyse empfohlen.'}
          </Alert>
        )}

        {hasMixedScoring && (
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            <AlertTitle>Scoring-Methodik geändert</AlertTitle>
            Diese Timeline enthält Tests mit unterschiedlichen Bewertungsmethoden (alt: 6 Branchen-Kriterien, neu: 7 AI-Visibility-Kategorien).
            Scores vor und nach der Umstellung sind nicht direkt vergleichbar. Die Korrelation berücksichtigt nur Tests mit der neuen Methodik.
          </Alert>
        )}

        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />

            {/* X-Axis */}
            <XAxis
              dataKey="date"
              tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
            />

            {/* Left Y-Axis - LLM Score */}
            <YAxis
              yAxisId="left"
              domain={[0, 100]}
              tick={{ fill: theme.palette.primary.main }}
              label={{
                value: 'LLM Score (%)',
                angle: -90,
                position: 'insideLeft',
                style: { fill: theme.palette.primary.main },
              }}
            />

            {/* Right Y-Axis - Benchmark Score */}
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={[0, 100]}
              tick={{ fill: theme.palette.secondary.main }}
              label={{
                value: 'Optimierungs-Score',
                angle: 90,
                position: 'insideRight',
                style: { fill: theme.palette.secondary.main },
              }}
            />

            <Tooltip content={<CustomTooltip />} />
            <Legend />

            {/* LLM Test Score Line */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="llmScore"
              name="LLM Score"
              stroke={theme.palette.primary.main}
              strokeWidth={3}
              dot={{ r: 6, fill: theme.palette.primary.main }}
              connectNulls={true}
              activeDot={{ r: 8 }}
              isAnimationActive={!reduceMotion}
              animationDuration={800}
              animationEasing="ease-out"
            />

            {/* Benchmark Test Score Line */}
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="optScore"
              name="Optimierungs-Score"
              stroke={theme.palette.secondary.main}
              strokeWidth={3}
              dot={{ r: 6, fill: theme.palette.secondary.main }}
              connectNulls={true}
              activeDot={{ r: 8 }}
              isAnimationActive={!reduceMotion}
              animationDuration={800}
              animationEasing="ease-out"
              animationBegin={100}
            />
          </ComposedChart>
        </ResponsiveContainer>

        <Box mt={2}>
          <Typography variant="caption" color="text.secondary">
            Korrelationskoeffizient (Pearson's r): {correlation !== null ? correlation.toFixed(3) : 'N/A'} |{' '}
            -1 = perfekte negative Korrelation, 0 = keine Korrelation, +1 = perfekte positive Korrelation
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CombinedTimelineChart;
